/**
 * How a grid presents its rows and columns.
 *
 * Two settings, and they have the same shape: both live on the view, both are
 * written with the same `PATCH`, and **neither changes which rows come back or
 * in what order**. That is why neither is in the row query's signature, and why
 * both are applied before the request rather than after -- a row that stays
 * short for a round trip after somebody asked for a taller one is a control
 * that reads as broken, and there is nothing to be wrong about while waiting.
 *
 * The row height does change the *geometry* the window is planned with -- a
 * taller row means fewer rows on screen and a different buffer -- but that
 * follows from the height reaching the grid, not from anything here: the store
 * replans when its viewport is reported, and the viewport is measured.
 *
 * The answer is not written back, for the reason a rename does not write it
 * back either: the endpoint serialises a view *without* its filters and
 * sortings, so storing it would quietly unfilter the view.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { View } from '@/api/types'
import type { RowHeightSize } from '../grid/columns'
import { patchView } from './view-cache'

/** The most columns the backend will freeze, the primary field included. */
export const MAX_FROZEN_COLUMNS = 4

export interface GridSettingsMutations {
  setRowHeight: (size: RowHeightSize) => void
  setFrozenColumns: (count: number) => void
  isPending: boolean
}

export function useGridSettings(tableId: number, viewId: number): GridSettingsMutations {
  const queryClient = useQueryClient()
  const key = qk.views(tableId)

  const change = useMutation({
    mutationFn: (body: Record<string, unknown>): Promise<unknown> =>
      unwrap(
        getApi().PATCH('/api/database/views/{view_id}/', {
          params: { path: { view_id: viewId } },
          // Cast at the boundary, like every view payload: the body is a union
          // over the view types and the compiler cannot know which one an id
          // names.
          body: body as never,
        }),
      ),

    onMutate: (body) => {
      const previous = queryClient.getQueryData<View[]>(key)
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined
          ? views
          : patchView(views, viewId, (view) => ({ ...view, ...body })),
      )
      return { previous }
    },

    onError: (_error, _body, context) => {
      queryClient.setQueryData<View[] | undefined>(key, context?.previous)
    },
  })

  return {
    setRowHeight: (size) => {
      change.mutate({ row_height_size: size })
    },
    setFrozenColumns: (count) => {
      // Unclamped, deliberately. A clamp here would be the same guard the menu
      // already is -- it does not offer a column past `MAX_FROZEN_COLUMNS`, and
      // there is no column before the first -- so nothing could ever reach it
      // and no test could tell it from its absence.
      change.mutate({ frozen_column_count: count })
    },
    isPending: change.isPending,
  }
}
